import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../../config/firebaseConfig';
import { isUserAdmin } from '../utilities/authUtils';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [adminLoading, setAdminLoading] = useState(false);

    useEffect(() => {
        let isMounted = true;
        let hasResolvedInitialAuth = false;

        const finishInitialLoading = () => {
            if (!hasResolvedInitialAuth && isMounted) {
                hasResolvedInitialAuth = true;
                setLoading(false);
            }
        };

        const fallbackTimer = setTimeout(() => {
            if (!isMounted || hasResolvedInitialAuth) {
                return;
            }

            setUser(auth.currentUser);
            setIsAdmin(false);
            setAdminLoading(false);
            finishInitialLoading();
        }, 10000);

        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (!isMounted) {
                return;
            }

            setUser(firebaseUser);
            finishInitialLoading();

            if (firebaseUser) {
                setAdminLoading(true);
                try {
                    const adminStatus = await isUserAdmin(firebaseUser.uid);
                    if (isMounted && auth.currentUser?.uid === firebaseUser.uid) {
                        setIsAdmin(adminStatus);
                    }
                } catch (error) {
                    console.error('Error loading admin status:', error);
                    if (isMounted) {
                        setIsAdmin(false);
                    }
                } finally {
                    if (isMounted) {
                        setAdminLoading(false);
                    }
                }
            } else {
                setIsAdmin(false);
                setAdminLoading(false);
            }
        }, (error) => {
            console.error('Auth state listener failed:', error);
            if (isMounted) {
                setUser(null);
                setIsAdmin(false);
                setAdminLoading(false);
                finishInitialLoading();
            }
        });

        return () => {
            isMounted = false;
            clearTimeout(fallbackTimer);
            unsubscribe();
        };
    }, []);

    const value = {
        user,
        isAdmin,
        loading,
        adminLoading
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
